import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { useParams } from "@tanstack/react-router";

import { IconArchiveDownload, IconArchiveUpload, IconArrowDown, IconArrowRight } from "@synthesis/shared-icons";
import { Button, EditArea, Tooltip } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { useQueryProject } from "../../../entity/project";
import { AccessDenied, PROJECT_ACCESS_DENIED_MESSAGE } from "../../components/access-denied";
import { useProjectAccessError, useUserRolesGGE } from "../../hooks";
import { InformationWidget, ModalArchiveProject, ModelsWidget } from "./local-components";

export const ProjectDashboardPage = observer(() => {
  const { projectId } = useParams({ strict: false });
  const { project, isLoading, isError, error } = useQueryProject(projectId);
  const isAccessError = useProjectAccessError(isError, error);
  const [title, setTitle] = useState<string>("");
  const [isEdit, setIsEdit] = useState(false);
  const [isInfoCollapsed, setIsInfoCollapsed] = useState(false);
  const roles = useUserRolesGGE();
  const isIntegration = project?.meta?.find((meta) => meta.key === "sourceSystem");

  useEffect(() => {
    setTitle(project?.title ?? "");
  }, [project?.title]);

  const updateTitle = async (value: string) => {
    setTitle(value.trim());
  };

  if (isLoading) {
    return (
      <div className="flex w-full flex-col px-8 pb-6 pt-8">
        <div className="bg-grey-300/30 mb-4 mr-4 w-60 animate-pulse rounded py-8" />
        <div className="bg-grey-300/30 mb-4 mr-4 animate-pulse rounded py-8" />
        <div className="bg-grey-300/30 mb-4 mr-4 animate-pulse rounded py-12" />
      </div>
    );
  }

  if (isAccessError) {
    return <AccessDenied message={PROJECT_ACCESS_DENIED_MESSAGE} />;
  }

  const fullRight = roles.СУПЕР || roles.АДМИНИСТРИРОВАНИЕ;
  const moderatorRight = !isIntegration && roles.МОДЕРИРОВАНИЕ;
  const disabledRight = !fullRight && !moderatorRight;

  return (
    <main className="flex h-[calc(100vh-70px)] flex-col px-8 pb-6 pt-8">
      <header className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex min-w-0 items-start gap-3">
            {disabledRight || !isEdit ? (
              <h1 className="heading-h4 whitespace-normal break-all">{project?.title}</h1>
            ) : (
              <EditArea className="heading-h4" disabled={project?.isArchived} onChange={updateTitle} defaultValue={project?.title || ""} />
            )}
            <Tooltip arrowSide="right" description={isInfoCollapsed ? "Показать информацию" : "Скрыть информацию"}>
              <Button
                size="sm"
                theme="secondary"
                className="shrink-0"
                leftIcon={isInfoCollapsed ? <IconArrowRight className="size-5" /> : <IconArrowDown className="size-5" />}
                onClick={() => setIsInfoCollapsed((prev) => !prev)}
                disabled={isEdit}
              />
            </Tooltip>
          </div>
          <ModalArchiveProject
            id={projectId ?? ""}
            title={project?.title ?? ""}
            isArchived={!!project?.isArchived}
            isIntegration={!!isIntegration}
            trigger={
              <Button
                disabled={disabledRight}
                leftIcon={project?.isArchived ? <IconArchiveUpload className="size-5" /> : <IconArchiveDownload className="size-5" />}
                size="sm"
                className="ml-3 flex w-fit"
              >
                {project?.isArchived ? "Вернуть из архива" : "Отправить в архив"}
              </Button>
            }
          />
        </div>
        {!isInfoCollapsed && (
          <div className="flex gap-4">
            <InformationWidget
              className="mb-8 w-full"
              project={project}
              isLoading={isLoading}
              isIntegration={!!isIntegration}
              disabled={disabledRight}
              title={title}
              setIsEdit={setIsEdit}
              isEdit={isEdit}
              isEditOrganization={roles.СУПЕР || roles.АДМИНИСТРИРОВАНИЕ}
            />
          </div>
        )}
      </header>

      <ModelsWidget
        className={cn("mb-4 mr-4 min-h-0 w-full flex-1")}
        projectId={projectId ?? ""}
        roles={roles}
        archivedProject={!!project?.isArchived}
        isIntegration={!!isIntegration}
      />
    </main>
  );
});
